import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// --- Types (Combined into this file for minimum files) ---

interface SessionSummary {
  session_id: string;
  call_sid: string;
  stream_id: string;
  timestamp: string;
  customer_name?: string; // Added new field
  order_status?: string; // Added new field
  language?: string; // Added new field
  user_data?: Record<string, any>; // Added user data field
}

interface ChatLog {
  role: "system" | "assistant" | "user" | "tool";
  content: string | object;
  tool_calls?: ToolCall[];
  tool_call_id?: string;
}

interface ToolCall {
  id: string;
  function: {
    name: string;
    arguments: string; // JSON string
  };
  type: "function";
}

interface FunctionCall {
  function_name: string;
  arguments: Record<string, any> | {};
}

interface Cost {
  USD: number | null;
  INR: number | null;
}

interface CallDetails {
  duration_formatted: string | null;
  date_created_ist: string | null;
  from: string | null;
  to: string | null;
  status: string | null;
}

interface Insights {
  function_calls: FunctionCall[] | null;
  call_details: CallDetails | null;
  open_ai_cost: Cost | null;
  cartesia_cost: Cost | null;
  deepgram_cost: Cost | null;
  twilio_cost: Cost | null;
  total_cost: Cost | null;
}

interface SessionDetails {
  calling_status: string;
  session_id: string;
  call_sid: string;
  stream_id: string;
  timestamp: string;
  chat_logs: ChatLog[];
  insights: Insights | null;
  audio_url?: string; // Added audio_url field
  document?: Record<string, any>; // Additional metadata document
  user_data?: Record<string, any>;
}

interface ProcessedSessionSummary extends SessionSummary {
    phoneNumber: string | null; // Extracted 12-digit phone number
    date: string | null;
    time: string | null;
    dateTime: Date | null;
    displayLabel: string;
    calling_status?: string; // Added calling status
    user_data?: Record<string, any>; // Added user data
}

// --- Configuration ---
const API_BASE_URL = 'https://interim-cabdemo-module.ispgnet.com';

// --- API Fetching (native fetch) ---
const apiService = {
  fetchSessionList: async (): Promise<SessionSummary[]> => {
    const response = await fetch(`${API_BASE_URL}/get_session_call_id?collection_name=MaiDubai`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.sessions || [];
  },

  fetchSessionDetails: async (sessionId: string): Promise<SessionDetails> => {
    const response = await fetch(`${API_BASE_URL}/chat/${sessionId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  },
};

// --- Helper Functions ---
const formatCurrency = (value: number | null | undefined, currency: 'USD' | 'INR'): string => {
    if (typeof value !== 'number') return currency === 'USD' ? '$--' : '₹--';
    return currency === 'USD'
      ? `$${value.toFixed(4)}`
      : `₹${value.toFixed(2)}`;
};

const getCostValue = (cost: Cost | null | undefined, currency: 'USD' | 'INR'): number => {
    if (!cost) return 0;
    const value = cost[currency];
    return typeof value === 'number' ? value : 0;
};

const parseArguments = (argsString: string): Record<string, any> | string => {
    try {
        // Try parsing outer JSON string first, if content itself is a JSON string representation
        let potentialJson = argsString;
        try {
            const parsedOuter = JSON.parse(argsString);
            if (typeof parsedOuter === 'string') {
                potentialJson = parsedOuter;
            }
        } catch (outerError) { /* Ignore if outer isn't JSON */ }

        // Parse the actual content or the result from outer parsing
        return JSON.parse(potentialJson);
    } catch (e) {
        // If it fails at any point, return the original string
        return argsString;
    }
};

// --- Main Component ---
const CustomerAnalyticsDashboard: React.FC = () => {
  const [sessions, setSessions] = useState<ProcessedSessionSummary[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<ProcessedSessionSummary[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionData, setSelectedSessionData] = useState<SessionDetails | null>(null);
  const [activeTab, setActiveTab] = useState<'sessions' | 'cost' | 'log' | 'flow'>('sessions');
  const [isLoadingList, setIsLoadingList] = useState<boolean>(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [audioIsPlaying, setAudioIsPlaying] = useState<boolean>(false);

  // --- Filtering State ---
  const [filterText, setFilterText] = useState<string>('');
  const [filterDate, setFilterDate] = useState<string>(''); // YYYY-MM-DD
  const [filterTime, setFilterTime] = useState<string>(''); // HH:MM (partial match)
  const [filterPhoneNumber, setFilterPhoneNumber] = useState<string>(''); // Selected phone number filter
  const [sessionsTableFilter, setSessionsTableFilter] = useState<string>(''); // Filter for sessions table

  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState<boolean>(false);
  const [isPhoneDropdownOpen, setIsPhoneDropdownOpen] = useState<boolean>(false);

  // --- Derived Data ---
  const uniquePhoneNumbers = useMemo(() => {
    const numbers = new Set<string>();
    sessions.forEach(s => {
      if (s.phoneNumber) {
        numbers.add(s.phoneNumber);
      }
    });
    return Array.from(numbers).sort();
  }, [sessions]);

// Process raw sessions to add derived fields
const processSessions = useCallback((rawSessions: SessionSummary[]): ProcessedSessionSummary[] => {
  return rawSessions.map(session => {
      // Extract ALL digits before the first underscore as the phone number
      // RegEx: ^(\d+) means: start of string (^), capture one or more digits (\d+), followed by an underscore (_)
      const phoneMatch = session.session_id.match(/^(\d+)_/); // <--- MODIFIED REGEX
      const phoneNumber = phoneMatch ? phoneMatch[1] : null; // phoneMatch[1] contains the captured digits

      let date: string | null = null;
      let time: string | null = null;
      let dateTime: Date | null = null;
       try {
          // Use the session's timestamp field for date/time info
          const dt = new Date(session.timestamp);
          if (!isNaN(dt.getTime())) {
              dateTime = dt;
              date = dt.toISOString().split('T')[0]; // YYYY-MM-DD
              time = dt.toTimeString().split(' ')[0]; // HH:MM:SS
          }
      } catch (e) { console.error("Timestamp parse error:", session.timestamp, e); }

      // Improved display label, handles missing phone number better
      const phonePart = phoneNumber ? `${phoneNumber}` : "UnknownPhone";
      const datePart = date ?? 'N/A';
      const timePart = time ?? 'N/A';
      const idPart = `...${session.session_id.slice(-6)}`; // Keep last 6 chars of ID for uniqueness hint
      const displayLabel = `${phonePart} (${datePart} ${timePart}) - ID:${idPart}`;

      return { ...session, phoneNumber, date, time, dateTime, displayLabel };
  });
}, []);

  // Initial fetch for session list
  useEffect(() => {
    const loadSessions = async () => {
      setIsLoadingList(true);
      setError(null);
      try {
        const rawSessionList = await apiService.fetchSessionList();
        const processed = processSessions(rawSessionList);
        setSessions(processed);
        // Don't set filteredSessions here, let the filter useEffect handle it
      } catch (err: any) {
        setError(`Failed to load session list: ${err.message}`);
        console.error(err);
      } finally {
        setIsLoadingList(false);
      }
    };
    loadSessions();
  }, [processSessions]);

  // Fetch details when session selection changes
  useEffect(() => {
    if (!selectedSessionId) {
      setSelectedSessionData(null);
      return;
    }
    const loadDetails = async () => {
      setIsLoadingDetails(true);
      setError(null);
      try {
        const details = await apiService.fetchSessionDetails(selectedSessionId);
        setSelectedSessionData(details);
        setActiveTab('cost'); // Default to cost tab when selecting a session
      } catch (err: any) {
        setError(`Failed to load details: ${err.message}`);
        setSelectedSessionData(null);
        console.error(err);
      } finally {
        setIsLoadingDetails(false);
      }
    };
    loadDetails();
  }, [selectedSessionId]);

  // Combined Filtering Logic
  useEffect(() => {
    let results = sessions;

     // Filter by selected Phone Number (exact match)
    if (filterPhoneNumber) {
        results = results.filter(session => session.phoneNumber === filterPhoneNumber);
    }

    // Filter by Date (exact match)
    if (filterDate) {
      results = results.filter(session => session.date === filterDate);
    }

    // Filter by Time (contains)
     if (filterTime) {
        // Simple check if HH:MM:SS contains the filter string
        results = results.filter(session => session.time?.includes(filterTime));
     }

    // Filter by General Search Text (case-insensitive contains)
    if (filterText) {
      const lowerCaseFilter = filterText.toLowerCase();
      results = results.filter(session =>
        session.session_id.toLowerCase().includes(lowerCaseFilter) ||
        session.call_sid.toLowerCase().includes(lowerCaseFilter) ||
        session.stream_id.toLowerCase().includes(lowerCaseFilter) ||
        session.displayLabel.toLowerCase().includes(lowerCaseFilter) ||
        session.order_status?.toLowerCase().includes(lowerCaseFilter) ||
        session.language?.toLowerCase().includes(lowerCaseFilter)
      );
    }

    setFilteredSessions(results);
  }, [filterText, filterDate, filterTime, filterPhoneNumber, sessions]);

  // --- Event Handlers ---
  const handleSessionSelect = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setFilterText('');
    setIsSearchDropdownOpen(false);
  };

  const handlePhoneNumberSelect = (phoneNumber: string) => {
    setFilterPhoneNumber(phoneNumber);
    setIsPhoneDropdownOpen(false);
  };

  const clearFilters = () => {
    setFilterText('');
    setFilterDate('');
    setFilterTime('');
    setFilterPhoneNumber('');
    setSelectedSessionId(null);
    setActiveTab('sessions');
  };

  // --- Inline Render Functions for Tabs ---

  const renderSessionsTable = () => {
    // Apply additional filtering for the sessions table
    const filteredForTable = sessionsTableFilter 
      ? filteredSessions.filter(session => 
          session.session_id.toLowerCase().includes(sessionsTableFilter.toLowerCase()) ||
          session.call_sid.toLowerCase().includes(sessionsTableFilter.toLowerCase()) ||
          session.stream_id.toLowerCase().includes(sessionsTableFilter.toLowerCase()) ||
          (session.phoneNumber && session.phoneNumber.includes(sessionsTableFilter))
        )
      : filteredSessions;

    return (
      <div className="p-4 overflow-x-auto">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">All Sessions</h2>
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Filter sessions..."
              value={sessionsTableFilter}
              onChange={(e) => setSessionsTableFilter(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {sessionsTableFilter && (
              <button
                onClick={() => setSessionsTableFilter('')}
                className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {filteredForTable.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No sessions found matching your criteria.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Name</th> {/* New Header */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calling Status</th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Call SID</th> */}
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Order Status</th> {/* New Header */}
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking ID</th>  */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Language</th> {/* New Header */}                  
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredForTable.map((session) => (
                  <tr key={session.session_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"> {/* New Cell */}
                      {session.customer_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                      {session.user_data?.phone_number || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.date || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.time ? session.time.substring(0, 8) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono truncate max-w-xs">
                      {session.session_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono truncate max-w-xs">
                      {session.calling_status || 'N/A'}
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono truncate max-w-xs">
                      {session.call_sid}
                    </td> */}
                    <td className="text-center align-middle">
                      <span
                        className={`px-2 py-0.5 rounded-full text-sm font-medium ${
                          session.order_status === 'Order Placed' 
                            ? 'bg-green-100 text-green-800'
                            : session.order_status === 'Order Failed'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                          {session.order_status === 'T/D'
                          ? 'Testing & Development'
                          : session.order_status || 'N/A'}  
                      </span>
                    </td>
                    {/*<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"> 
                      {session.booking_id || 'N/A'}
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800"> {/* New Cell */}
                      {session.language || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleSessionSelect(session.session_id)}
                        className="text-blue-600 hover:text-blue-900 font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderCostAnalysis = () => {
    if (!selectedSessionData) return null;
    const insights = selectedSessionData.insights;
    if (!insights) return <p className="p-6 text-center text-gray-500">Cost insights not available.</p>;

    // --- Cost Data Preparation ---
    const costItems = [
        { name: 'OpenAI', cost: insights.open_ai_cost, color: 'bg-primary' },      // Assign Tailwind colors
        { name: 'Cartesia', cost: insights.cartesia_cost, color: 'bg-green-500' },
        { name: 'Deepgram', cost: insights.deepgram_cost, color: 'bg-yellow-500' },
        { name: 'Twilio', cost: insights.twilio_cost, color: 'bg-red-500' },
    ].filter(item => getCostValue(item.cost, 'USD') > 0 || getCostValue(item.cost, 'INR') > 0);

     const totalUSD = getCostValue(insights.total_cost, 'USD');
     const totalINR = getCostValue(insights.total_cost, 'INR');

    // --- Enhanced Cost Breakdown Visualization ---
    const renderCostBreakdownVisualization = () => {
        if (costItems.length === 0) {
          return <p className="text-sm text-gray-500 mt-4 text-center">No detailed cost breakdown available.</p>;
        }
      
        const COLORS = [
            '#3B82F6', // Blue - OpenAI
            '#10B981', // Green - Cartesia
            '#FBBF24', // Yellow - Deepgram
            '#EF4444', // Red - Twilio
            '#8B5CF6', // Purple - Additional
            '#F472B6', // Pink - Additional
          ];
      
        return (
          <div className="mt-4">
            <h5 className="font-semibold text-gray-600 text-center text-sm mb-3">Cost Breakdown</h5>
            <div className="w-full h-64">
                <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                        <Pie
                        data={costItems.map(item => ({
                            name: item.name,
                            value: getCostValue(item.cost, 'USD'),
                            cost: item.cost,
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                        isAnimationActive={true}
                        >
                        {costItems.map((item, index) => (
                            <Cell key={item.name} fill={COLORS[index % COLORS.length]} />
                        ))}
                        </Pie>

                        <Tooltip
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                const { name, value, payload: itemPayload } = payload[0];
                                // Ensure value is treated as a number
                                const numericValue = typeof value === 'number' ? value : Number(value) || 0;
                                const inr = getCostValue(itemPayload.cost, 'INR');
                                
                                return (
                                    <div className="bg-white border shadow rounded px-3 py-2 text-sm text-gray-700">
                                    <div className="font-semibold mb-1">{name}</div>
                                    <div><span className="text-gray-500">USD:</span> ${numericValue.toFixed(4)}</div>
                                    <div><span className="text-gray-500">INR:</span> ₹{inr.toFixed(2)}</div>
                                    </div>
                                );
                                }
                                return null;
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
          </div>
        );
      };
      
    // Audio Player Component
    const renderAudioPlayer = () => {
      if (!selectedSessionData.audio_url) {
        return (
          <div className="bg-gray-50 p-4 rounded-lg mt-4 text-center text-gray-500">
            No audio recording available for this session.
          </div>
        );
      }
    
      return (
        <div className="bg-gray-50 p-4 rounded-lg mt-4 border border-gray-200">
          <h4 className="font-semibold text-base md:text-lg mb-3 border-b pb-2 text-gray-700">Session Recording</h4>
          <div className="flex flex-col">
            <audio
              controls
              className="w-full"
              onPlay={() => setAudioIsPlaying(true)}
              onPause={() => setAudioIsPlaying(false)}
              onEnded={() => setAudioIsPlaying(false)}
            >
              <source src={selectedSessionData.audio_url} type="audio/wav" />
              Your browser does not support the audio element.
            </audio>
            <div className="mt-2 text-xs text-gray-500 flex items-center">
              {audioIsPlaying ? (
                <span className="flex items-center text-blue-600">
                  <span className="relative flex h-3 w-3 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                  </span>
                  Playing...
                </span>
              ) : (
                <span className="flex items-center">
                  <svg className="h-3 w-3 mr-1 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Listen to call recording
                </span>
              )}
            </div>
          </div>
        </div>
      );
    };
    

    interface DetailsSectionProps {
        title: string;
        data: any; // or define the type of data if you know it
      }
    const DetailsSection = ({ title, data }:DetailsSectionProps) => {
      if (!data) {
        return null;
      }

      return (
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h4 className="font-semibold text-base md:text-lg mb-3 border-b pb-2 text-gray-700">{title}</h4>
          {Object.entries(data).map(([key, value]) => {
            if (key === 'user_data' || key === 'session_id' || key === 'call_sid' || key === 'stream_id' || key === 'timestamp' || key === 'call_status' || key === 'end_call') {
              return null;
            }

            if (key === 'topic' && Array.isArray(value)) {
              return (
                <div key={key} className="text-xs md:text-sm text-gray-600 mb-1 break-all">
                  <strong>Topics spoke:</strong>
                  <ul className="list-disc pl-5">
                    {value.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            }

            if (typeof value === 'object' && value !== null) {
              return Object.entries(value).map(([subKey, subValue]) => (
                // <p key={`${key}-${subKey}`} className="text-xs md:text-sm text-gray-600 mb-1 break-all">
                //   <strong>{subKey}:</strong> {JSON.stringify(subValue) ?? 'N/A'}
                // </p>
                null
              ));
            } else {
              return (
                <p key={key} className="text-xs md:text-sm text-gray-600 mb-1 break-all">
                  <strong>{key}:</strong> {value?.toString() ?? 'N/A'}
                </p>
              );
            }
          })}
        </div>
      );
    };

    return (
        <div className="grid grid-cols-1 gap-4 p-4 md:p-6">
          {/* Audio Player Section - At the top of the cost analysis */}
          {renderAudioPlayer()}

          {/* Metadata and Cost Summary in a two-column layout */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
            {/* Metadata */}
            <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
              <h4 className="font-semibold text-base md:text-lg mb-3 border-b pb-2 text-gray-700">Session Metadata</h4>
              <p className="text-xs md:text-sm text-gray-600 mb-1 break-all"><strong>Session ID:</strong> {selectedSessionData.session_id}</p>
              <p className="text-xs md:text-sm text-gray-600 mb-1 break-all"><strong>Call SID:</strong> {selectedSessionData.call_sid ?? 'N/A'}</p>
              <p className="text-xs md:text-sm text-gray-600 mb-1 break-all"><strong>Stream ID:</strong> {selectedSessionData.stream_id ?? 'N/A'}</p>
              <p className="text-xs md:text-sm text-gray-600 mb-1"><strong>Timestamp:</strong> {insights.call_details?.date_created_ist ?? selectedSessionData.timestamp ?? 'N/A'}</p>
              <p className="text-xs md:text-sm text-gray-600 mb-1"><strong>Duration:</strong> {insights.call_details?.duration_formatted ?? 'N/A'}</p>
              <p className="text-xs md:text-sm text-gray-600"><strong>Status:</strong> {insights.call_details?.status ?? 'N/A'}</p>
            </div>

            {/* Order details */}
            <DetailsSection title="Order details" data={selectedSessionData.document} />

            {/* Vehicle details */}
            <DetailsSection title="Vehicle details" data={selectedSessionData.document?.vehicle_details} />

            {/* Financial details */}
            <DetailsSection title="Financial details" data={selectedSessionData.document?.financial_information} />

            {/* Personal details */}
            <DetailsSection title="Personal details" data={selectedSessionData.document?.personal_information} />

            {/* Purchase intent details */}
            <DetailsSection title="Purchase intent details" data={selectedSessionData.document?.purchase_intent} />

            {/* Appointment details */}
            <DetailsSection title="Appointment details" data={selectedSessionData.document?.appointment} />

            {/* Cost Summary & Visualization */}
            {/* <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-col">
              <div>
                <h4 className="font-semibold text-base md:text-lg mb-3 border-b pb-2 text-gray-700">Cost Summary</h4>
                <p className="text-base md:text-lg text-gray-800 mb-2">Total (USD): <strong className="font-mono">{formatCurrency(totalUSD, 'USD')}</strong></p>
                <p className="text-base md:text-lg text-gray-800">Total (INR): <strong className="font-mono">{formatCurrency(totalINR, 'INR')}</strong></p>
              </div>
              <div className="mt-auto pt-4">
                {renderCostBreakdownVisualization()}
              </div>
            </div> */}
          </div>
        </div>
      );
    };
  const renderConversationLog = () => {
    if (!selectedSessionData?.chat_logs || selectedSessionData.chat_logs.length === 0) {
      return <p className="p-6 text-center text-gray-500">No chat logs available.</p>;
    }

    const roleColors: Record<ChatLog['role'], string> = {
        system: 'bg-gray-200 text-gray-700 border-gray-300',
        assistant: 'bg-blue-100 text-blue-800 border-blue-200',
        user: 'bg-indigo-100 text-indigo-800 border-indigo-200',
        tool: 'bg-amber-100 text-amber-800 border-amber-200',
    };
     const toolResultBg = 'bg-purple-100 text-purple-800 border-purple-200';

    return (
      <div className="p-3 md:p-4 space-y-3 h-[calc(100vh-150px)] overflow-y-auto bg-gray-50">
        {selectedSessionData.chat_logs.map((msg: ChatLog, index: number) => {
          if (msg.role === 'system') {
            return null;
          }
          const isUser = msg.role === 'user';
          const alignment = isUser ? 'justify-end' : 'justify-start';
          let bubbleStyle = `max-w-[80%] p-3 rounded-lg shadow-sm border`;
          let roleLabel: string | null = msg.role;
          let content: React.ReactNode = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);

          bubbleStyle += ` ${roleColors[msg.role] || roleColors.system}`;

           if (msg.role === 'assistant' && msg.tool_calls) {
                content = (
                    <>
                       {typeof msg.content === 'string' && msg.content.trim() && <p className="mb-2 text-sm">{msg.content}</p>}
                       {msg.tool_calls.map((call) => (
                           <div key={call.id} className="mt-1 border-l-4 border-yellow-400 pl-2 py-1 bg-yellow-50 rounded">
                               <div className="text-xs font-semibold text-yellow-700">Function Call Attempt:</div>
                               <pre className="text-xs text-yellow-900 font-mono whitespace-pre-wrap break-all mt-1">
                                  {`Func: ${call.function.name}\nArgs: ${JSON.stringify(parseArguments(call.function.arguments), null, 1)}`}
                               </pre>
                           </div>
                       ))}
                    </>
                );
                roleLabel = 'Assistant (Tool Call)';
           }
           else if (msg.role === 'tool' && msg.tool_call_id) {
                let toolContentStr = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
                 try {
                    const parsedJson = JSON.parse(toolContentStr);
                    toolContentStr = JSON.stringify(parsedJson, null, 1);
                 } catch (e) { /* ignore */ }

                content = (
                     <>
                         <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                            {toolContentStr}
                         </pre>
                    </>
                 );
                roleLabel = `Function Result (ID: ${msg.tool_call_id})`;
                bubbleStyle = `max-w-[80%] p-3 rounded-lg shadow-sm border ${toolResultBg}`;
           }

          return (
            <div key={index} className={`flex ${alignment}`}>
              <div className={bubbleStyle}>
                {!isUser && <span className="block text-xs font-bold mb-1 opacity-70 capitalize">{roleLabel}</span>}
                <div className="text-sm prose prose-sm max-w-none">{content}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

 const renderSessionFlows = () => {
     const functionCalls = selectedSessionData?.insights?.function_calls;
    if (!functionCalls || functionCalls.length === 0) {
      return <p className="p-6 text-center text-gray-500">No function calls recorded.</p>;
    }

    return (
        <div className="p-4 md:p-6 h-[calc(100vh-150px)] overflow-y-auto bg-gradient-to-br from-gray-50 to-blue-50">
             <h3 className="text-lg md:text-xl font-semibold text-center mb-6 text-gray-700 tracking-wide">Function Call Sequence</h3>
             <div className="relative pl-5 md:pl-8">
                 <div className="absolute left-5 md:left-8 top-2 bottom-0 w-0.5 bg-blue-300 z-0" aria-hidden="true"></div>

                {functionCalls.map((call: FunctionCall, index: number) => (
                     <div key={index} className="relative mb-6 md:mb-8 pl-6 md:pl-8">
                          <div className="absolute left-[-6px] md:left-[-4px] top-[5px] w-4 h-4 bg-white border-2 border-blue-500 rounded-full z-10" aria-hidden="true"></div>

                         <div className="bg-white p-3 md:p-4 rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200">
                             <div className="flex items-center mb-2">
                                 <span className={`flex-shrink-0 ${index % 2 === 0 ? 'bg-teal-500' : 'bg-purple-500'} text-white rounded-md w-6 h-6 text-xs font-bold flex items-center justify-center mr-3 shadow-sm`}>{index + 1}</span>
                                 <span className="font-semibold text-gray-800 text-sm md:text-base">{call.function_name}</span>
                             </div>
                             <div className="pl-9">
                                 <label className="text-xs font-semibold text-gray-500 block mb-1">Arguments:</label>
                                 <pre className="text-xs bg-gray-50 p-2 rounded border border-gray-200 font-mono whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                                     {JSON.stringify(call.arguments, null, 2)}
                                 </pre>
                             </div>
                         </div>
                     </div>
                 ))}
             </div>
         </div>
    );
  };


  // --- Main Render ---
  return (
    <div className="flex flex-col h-screen bg-gray-100 overflow-hidden ">
      {/* Header - Sticky */}
      <header className="bg-white border-b border-gray-200  sticky top-0 z-30 px-3 md:px-4 py-2 flex items-center justify-between flex-wrap ">
        <h1 className="text-lg md:text-xl font-bold text-gray-800 whitespace-nowrap mr-4">Session Analytics</h1>
         {/* Filter Controls Container */}
        <div className="flex items-center space-x-1 md:space-x-3 mt-1 md:mt-0 flex-wrap">
           {/* Custom Phone Number Dropdown */}
           <div className="relative min-w-[140px] md:min-w-[160px]">
                <button
                    onClick={() => setIsPhoneDropdownOpen(!isPhoneDropdownOpen)}
                    onBlur={() => setTimeout(() => setIsPhoneDropdownOpen(false), 150)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white text-left flex justify-between items-center h-[42px]"
                    title="Filter by Phone Number"
                >
                    <span className={`truncate ${filterPhoneNumber ? 'text-gray-800' : 'text-gray-400'}`}>
                        {filterPhoneNumber || "Phone Number"}
                    </span>
                    <svg className={`w-4 h-4 ml-1 text-gray-400 transition-transform duration-200 ${isPhoneDropdownOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </button>
                 {isPhoneDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto z-40">
                       {uniquePhoneNumbers.length > 0 ? (
                         uniquePhoneNumbers.map((phone) => (
                            <div
                                key={phone}
                                onMouseDown={() => handlePhoneNumberSelect(phone)}
                                className={`p-2 text-sm hover:bg-blue-100 cursor-pointer ${filterPhoneNumber === phone ? 'bg-blue-100 font-semibold' : ''}`}
                             >
                               {phone}
                            </div>
                         ))
                       ) : (
                         <div className="p-2 text-gray-500 text-sm">No numbers found.</div>
                       )}
                    </div>
                 )}
           </div>

           {/* Custom Searchable Input */}
           <div className="relative min-w-[200px] md:min-w-[300px]">
                <input
                    type="text"
                    placeholder={isLoadingList ? "Loading..." : "Search ID, SID, Label..."}
                    value={filterText}
                    onChange={(e) => { setFilterText(e.target.value); if(e.target.value) setIsSearchDropdownOpen(true);}}
                    onFocus={() => setIsSearchDropdownOpen(true)}
                    disabled={isLoadingList}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm h-[42px]"
                 />
                 {isSearchDropdownOpen && filterText && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto z-30">
                       {isLoadingList ? ( <div className="p-2 text-gray-500 text-sm">Loading...</div>)
                        : filteredSessions.length > 0 ? (
                         filteredSessions.slice(0, 50).map((session) => (
                            <div
                                key={session.session_id}
                                onMouseDown={() => handleSessionSelect(session.session_id)}
                                className="p-2 text-sm hover:bg-blue-100 cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis"
                                title={session.displayLabel}
                             >
                               {session.displayLabel}
                            </div>
                         ))
                       ) : !filterText ? ( <div className="p-2 text-gray-500 text-sm">Type to search...</div> )
                       : ( <div className="p-2 text-gray-500 text-sm">No sessions match.</div> )
                       }
                    </div>
                 )}
           </div>

           {/* Date Filter */}
           <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm h-[42px] w-[130px]" title="Filter by date"/>
           {/* Time Filter (Basic) */}
           <input type="time" value={filterTime} onChange={(e) => setFilterTime(e.target.value)} step="1" className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm h-[42px] w-[100px]" title="Filter by time (HH:MM partial match)"/>

           {/* Clear Filters Button */}
           <button onClick={clearFilters} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-100 rounded-md h-[42px] transition-colors" title="Clear All Filters">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
           </button>
        </div>
      </header>

      {/* Tabs - Sticky */}
      <nav className="bg-gray-100 border-b border-gray-300 sticky top-[65px] md:top-[69px] z-20 px-2 md:px-4 flex space-x-1 md:space-x-2">
        {([
          { id: 'sessions', label: 'All Sessions' },
          { id: 'cost', label: 'Analysis' },
          { id: 'log', label: 'Conversation Log' },
          { id: 'flow', label: 'Session Flow' },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            disabled={(tab.id !== 'sessions' && !selectedSessionId) || isLoadingDetails}
            className={`py-2 px-3 md:px-4 text-xs md:text-sm font-medium border-b-2 transition-colors duration-150 focus:outline-none ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-400'
            } disabled:text-gray-300 disabled:hover:border-transparent disabled:cursor-not-allowed`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Content Area - Scrollable */}
      <main className="flex-grow overflow-y-auto bg-gray-50">
         {isLoadingList && !sessions.length ? (
             <div className="p-6 text-center text-gray-500">Loading sessions...</div>
         ): isLoadingDetails ? (
            <div className="p-6 text-center text-gray-500">Loading details...</div>
         ) : error ? (
            <div className="p-6 text-center text-red-600 bg-red-50 border border-red-200 rounded m-4">{error}</div>
         ) : activeTab === 'sessions' ? (
            renderSessionsTable()
         ) : !selectedSessionId ? (
            <div className="p-6 text-center text-gray-500">Select a session to view details.</div>
         ) : !selectedSessionData ? (
             <div className="p-6 text-center text-gray-500">Could not load session data.</div>
         ) : (
          <>
            {activeTab === 'cost' && renderCostAnalysis()}
            {activeTab === 'log' && renderConversationLog()}
            {activeTab === 'flow' && renderSessionFlows()}
          </>
         )}
         {/* Fallback if no sessions match filters */}
         {!isLoadingList && activeTab === 'sessions' && filteredSessions.length === 0 && (filterPhoneNumber || filterDate || filterTime || filterText) && (
             <div className="p-6 text-center text-gray-500">No sessions match the current filters.</div>
         )}
      </main>

        {/* Clickaway listener for dropdowns */}
        {(isSearchDropdownOpen || isPhoneDropdownOpen) && (
            <div
                className="fixed inset-0 z-20"
                onClick={() => {
                    setIsSearchDropdownOpen(false);
                    setIsPhoneDropdownOpen(false);
                }}
            ></div>
        )}
    </div>
  );
};

export default CustomerAnalyticsDashboard;